import React, { useState } from 'react';
import { Search, Filter, Shield, AlertTriangle, MessageSquare, CheckCircle2, XCircle, Eye, Flag, UserX, FileText } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
interface AdminModerationCenterProps {
  complaints: any[];
  reviews: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
}
export function AdminModerationCenter({
  complaints,
  reviews,
  setActiveTab,
  setDetailPanel,
  success
}: AdminModerationCenterProps) {
  const [activeTab, setLocalActiveTab] = useState('complaints');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all'
  });
  const safeLC = (val: any) => String(val ?? '').toLowerCase();
  const filteredComplaints = (complaints || []).filter(c => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = safeLC(c.id).includes(q) || safeLC(c.author).includes(q) || safeLC(c.subject).includes(q);
    if (!matchesSearch) return false;
    if (filters.status !== 'all' && c.status !== filters.status) return false;
    if (filters.priority !== 'all' && c.priority !== filters.priority) return false;
    return true;
  });
  const filteredReviews = (reviews || []).filter(r => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = safeLC(r.id).includes(q) || safeLC(r.author).includes(q) || safeLC(r.astrologer).includes(q);
    if (!matchesSearch) return false;
    if (filters.status !== 'all' && r.status !== filters.status) return false;
    return true;
  });
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <Badge variant="error" size="sm" data-id="element-5374">
            Высокий
          </Badge>;
      case 'normal':
        return <Badge variant="warning" size="sm" data-id="element-5375">
            Средний
          </Badge>;
      case 'low':
        return <Badge variant="neutral" size="sm" data-id="element-5376">
            Низкий
          </Badge>;
      default:
        return <Badge variant="neutral" size="sm" data-id="element-5377">
            {priority}
          </Badge>;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="error" size="sm" data-id="element-5378">
            Новая
          </Badge>;
      case 'in_progress':
        return <Badge variant="warning" size="sm" data-id="element-5379">
            В работе
          </Badge>;
      case 'resolved':
        return <Badge variant="success" size="sm" data-id="element-5380">
            Решена
          </Badge>;
      case 'closed':
        return <Badge variant="neutral" size="sm" data-id="element-5381">
            Закрыта
          </Badge>;
      // For reviews
      case 'pending':
        return <Badge variant="warning" size="sm" data-id="element-5382">
            На проверке
          </Badge>;
      case 'approved':
        return <Badge variant="success" size="sm" data-id="element-5383">
            Опубликован
          </Badge>;
      case 'rejected':
        return <Badge variant="error" size="sm" data-id="element-5384">
            Отклонен
          </Badge>;
      case 'hidden':
        return <Badge variant="neutral" size="sm" data-id="element-5385">
            Скрыт
          </Badge>;
      default:
        return <Badge variant="neutral" size="sm" data-id="element-5386">
            {status}
          </Badge>;
    }
  };
  return <div className="space-y-6" data-id="element-5387">
      <div className="flex justify-between items-center" data-id="element-5388">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-5389">
          <Shield className="w-6 h-6 text-indigo-600" data-id="element-5390" /> Центр модерации
        </h2>
      </div>

      <Tabs tabs={[{
      id: 'complaints',
      label: `Жалобы (${complaints.filter(c => c.status === 'new').length} новых)`
    }, {
      id: 'reviews',
      label: `Отзывы (${reviews.filter(r => r.status === 'pending').length} на проверке)`
    }, {
      id: 'risk_log',
      label: 'Журнал нарушений'
    }]} activeTab={activeTab} onChange={setLocalActiveTab} data-id="element-5391" />

      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-hidden" data-id="element-5392">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50" data-id="element-5393">
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-5394">
            <div className="relative flex-1 sm:w-64" data-id="element-5395">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-5396" />
              <input type="text" placeholder="Поиск..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5397" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-5399" />} onClick={() => setShowFilters(!showFilters)} data-id="element-5398">
              Фильтры
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && <div className="p-4 border-b border-slate-100 bg-white space-y-4 animate-in slide-in-from-top-2" data-id="element-5400">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-id="element-5401">
              <div data-id="element-5402">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-5403">
                  Статус
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-5404">
                  <option value="all" data-id="element-5405">Все</option>
                  {activeTab === 'complaints' ? <>
                      <option value="new" data-id="element-5406">Новая</option>
                      <option value="in_progress" data-id="element-5407">В работе</option>
                      <option value="resolved" data-id="element-5408">Решена</option>
                      <option value="closed" data-id="element-5409">Закрыта</option>
                    </> : activeTab === 'reviews' ? <>
                      <option value="pending" data-id="element-5410">На проверке</option>
                      <option value="approved" data-id="element-5411">Опубликован</option>
                      <option value="rejected" data-id="element-5412">Отклонен</option>
                      <option value="hidden" data-id="element-5413">Скрыт</option>
                    </> : null}
                </select>
              </div>
              {activeTab === 'complaints' && <div data-id="element-5414">
                  <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-5415">
                    Приоритет
                  </label>
                  <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.priority} onChange={e => setFilters({
              ...filters,
              priority: e.target.value
            })} data-id="element-5416">
                    <option value="all" data-id="element-5417">Все</option>
                    <option value="high" data-id="element-5418">Высокий</option>
                    <option value="normal" data-id="element-5419">Средний</option>
                    <option value="low" data-id="element-5420">Низкий</option>
                  </select>
                </div>}
              <div className="flex items-end" data-id="element-5421">
                <Button variant="ghost" size="sm" onClick={() => setFilters({
              status: 'all',
              priority: 'all'
            })} data-id="element-5422">
                  Сбросить
                </Button>
              </div>
            </div>
          </div>}

        <div className="overflow-x-auto" data-id="element-5423">
          {activeTab === 'complaints' && <table className="w-full text-left" data-id="element-5424">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-xs uppercase tracking-wider" data-id="element-5425">
                <tr data-id="element-5426">
                  <th className="px-4 py-3 font-medium" data-id="element-5427">ID / Дата</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5428">Автор</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5429">Объект жалобы</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5430">Тема</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5431">Приоритет</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5432">Статус</th>
                  <th className="px-4 py-3 text-right" data-id="element-5433">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-5434">
                {filteredComplaints.map(c => <tr key={c.id} className="hover:bg-slate-50 transition-colors" data-id="element-5435">
                    <td className="px-4 py-3" data-id="element-5436">
                      <div className="text-xs font-bold text-slate-900" data-id="element-5437">
                        {c.id}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-5438">{c.date}</div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium" data-id="element-5439">
                      {c.user || c.from}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600" data-id="element-5440">
                      {c.target || 'Астролог'}
                    </td>
                    <td className="px-4 py-3" data-id="element-5441">
                      <div className="text-sm font-medium text-slate-900" data-id="element-5442">
                        {c.subject || c.type}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-[200px]" data-id="element-5443">
                        {c.desc || 'Описание отсутствует'}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-5444">
                      {getPriorityBadge(c.priority || 'normal')}
                    </td>
                    <td className="px-4 py-3" data-id="element-5445">{getStatusBadge(c.status)}</td>
                    <td className="px-4 py-3 text-right" data-id="element-5446">
                      <div className="flex justify-end gap-2" data-id="element-5447">
                        {c.status !== 'resolved' && <Button variant="outline" size="sm" className="h-7 text-xs text-emerald-600 border-emerald-200" onClick={() => success('Жалоба решена')} data-id="element-5448">
                            Решить
                          </Button>}
                        <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" data-id="element-5449">
                          <Eye className="w-4 h-4" data-id="element-5450" />
                        </Button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>}

          {activeTab === 'reviews' && <table className="w-full text-left" data-id="element-5451">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-xs uppercase tracking-wider" data-id="element-5452">
                <tr data-id="element-5453">
                  <th className="px-4 py-3 font-medium" data-id="element-5454">ID / Дата</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5455">Автор</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5456">Астролог</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5457">Оценка</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5458">Текст отзыва</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5459">Статус</th>
                  <th className="px-4 py-3 text-right" data-id="element-5460">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-5461">
                {filteredReviews.map(r => <tr key={r.id} className="hover:bg-slate-50 transition-colors" data-id="element-5462">
                    <td className="px-4 py-3" data-id="element-5463">
                      <div className="text-xs font-bold text-slate-900" data-id="element-5464">
                        REV-{r.id}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-5465">{r.date}</div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium" data-id="element-5466">
                      {r.reviewer}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600" data-id="element-5467">
                      {r.astrologer}
                    </td>
                    <td className="px-4 py-3" data-id="element-5468">
                      <div className="flex items-center gap-1" data-id="element-5469">
                        <span className="font-bold text-sm text-slate-900" data-id="element-5470">
                          {r.rating}
                        </span>
                        <span className="text-amber-400 text-sm" data-id="element-5471">★</span>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-5472">
                      <div className="text-sm text-slate-600 truncate max-w-[300px]" data-id="element-5473">
                        {r.text}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-5474">{getStatusBadge(r.status)}</td>
                    <td className="px-4 py-3 text-right" data-id="element-5475">
                      <div className="flex justify-end gap-1" data-id="element-5476">
                        {r.status === 'pending' && <>
                            <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-emerald-600 hover:bg-emerald-50" onClick={() => success('Отзыв опубликован')} data-id="element-5477">
                              <CheckCircle2 className="w-4 h-4" data-id="element-5478" />
                            </Button>
                            <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-rose-600 hover:bg-rose-50" onClick={() => success('Отзыв отклонен')} data-id="element-5479">
                              <XCircle className="w-4 h-4" data-id="element-5480" />
                            </Button>
                          </>}
                        <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" data-id="element-5481">
                          <Eye className="w-4 h-4" data-id="element-5482" />
                        </Button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>}

          {activeTab === 'risk_log' && <div className="p-8 text-center text-slate-500" data-id="element-5483">
              <Shield className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-5484" />
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-5485">
                Журнал нарушений
              </h3>
              <p className="text-sm max-w-md mx-auto" data-id="element-5486">
                Здесь отображается история всех автоматических срабатываний Risk
                Detection и ручных блокировок.
              </p>
            </div>}
        </div>
      </Card>
    </div>;
}